import React, { useEffect } from "react";
import { Table } from "antd";
import cx from "classnames";

// Hook baru: menggabungkan data antrian & shift dari satu endpoint
import useGetDisplayQueue from "@Hooks/queries/useGetDisplayQueue";
import useGetTodaysQueueInfo from "@Hooks/queries/useGetTodaysQueueInfo";

import EmergencyImage from "@Assets/icons/alarm.svg";
import TagStatus from "@Components/V2/TagStatus";
import QueueNumber from "@Pages/Admin/QueueManagement/Queue/Components/QueueNumber";
import { getPrimaryQueue } from "@Utils/queueHelper";

import PassedQueueMore from "./PassedQueueMore";
import ShiftInformation from "./ShiftInformation";
import "./ShowQueue.css";

const Queues = () => {
  // ─── STEP 1: Panggil hook baru ─────────────────────────────────────────────
  // Hook ini memanggil GET /admin/queues/getDisplayQueue
  // Secara otomatis refresh setiap 10 detik (refetchInterval: 10000)
  const { data: displayData, isLoading: isLoadingQueues } = useGetDisplayQueue({
    refetchInterval: 10000,
  });

  const todaysQueueInfo = useGetTodaysQueueInfo({ refetchInterval: 10000 });

  // TV Bro (Android TV) under-reports the viewport's effective CSS width,
  // which drops this page below the 1200px breakpoint and stacks the
  // columns, pushing tables below the fold where a TV remote can't scroll.
  // Force a fixed, known-wide viewport only while this TV display page is
  // mounted, and restore the original on unmount so every other page keeps
  // its normal responsive width=device-width viewport.
  useEffect(() => {
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) return undefined;
    const originalViewportContent = viewportMeta.getAttribute("content");
    viewportMeta.setAttribute("content", "width=1280, initial-scale=1");
    return () => {
      if (viewportMeta && originalViewportContent) {
        viewportMeta.setAttribute("content", originalViewportContent);
      }
    };
  }, []);

  // No shift running: stay on this page and show an idle state instead of
  // redirecting away. The 10 s polling above keeps running, so the queue
  // appears on its own once a clinic shift starts (stable URL for the TV).
  const noShiftRunning =
    todaysQueueInfo?.data?.message === "Tidak ada shift yang sedang berjalan" ||
    todaysQueueInfo?.data?.message === "No shifts are currently running!";

  console.log("displayData", displayData);

  // ─── STEP 2: Petakan data dari response ke variabel lokal ──────────────────
  // Ambil antrian berjalan (antrian_berjalan) dari response
  const queuesList = displayData?.antrian_berjalan || [];

  // Nomor antrian yang sedang dilayani sekarang
  const order = displayData?.current_queue || 0;

  // Nomor antrian yang berikutnya akan dipanggil
  const nextOrder = displayData?.next_queue || 0;

  // Antrian yang sudah terlewat (antrian_terlewat)
  // displayData?.antrian_terlewat ||
  const passedQueuesList = displayData?.antrian_terlewat || [];

  // Total antrian terlewat. antrian_terlewat dibatasi config.antrian_terlewat_count,
  // jadi bisa lebih pendek dari total.
  const passedCount = displayData?.passed_count ?? passedQueuesList.length;

  // Pesan pemberitahuan antrian terlewat
  const passedQueueNotice = displayData?.passed_queue_notice || "";

  // Info shift diambil dari item pertama di antrian_berjalan (jika ada)
  const firstQueueItem = queuesList[0] || null;
  const shiftName = firstQueueItem?.shift?.name || null;
  // Shift yang sedang berjalan: getTodaysQueueInfo diutamakan, antrian_berjalan sebagai cadangan
  const runningShift = {
    ...firstQueueItem?.shift,
    ...todaysQueueInfo?.data?.shift,
  };
  const doctorArrivalStatus = firstQueueItem?.shift?.doctor_arrival_status || 0;

  // ─── STEP 3: Hitung nilai turunan ──────────────────────────────────────────
  // Cari antrian berdasarkan nomor antriannya
  const findCurrentOrNextQueue = (queueNumber, queues) => {
    return (
      queues?.find((item) => item?.queue_number === queueNumber) || undefined
    );
  };

  // Ambil nama pasien berdasarkan nomor antrian
  const getPatientName = (queueNumber) => {
    const filterData =
      queuesList.find((item) => item?.queue_number === queueNumber) || null;
    return filterData?.child?.fullname || null;
  };

  const currIsAdminRec = findCurrentOrNextQueue(order, queuesList)?.is_passed;

  const nxtIsAdminRec = findCurrentOrNextQueue(
    nextOrder,
    queuesList,
  )?.is_passed;

  // Filter antrian emergency (status id = 8)
  const emergencyQueue = queuesList.filter(
    (item) => item.queue_status?.id === 8,
  );

  const products = queuesList;
  const currentIsAdminRecommended = !!currIsAdminRec;
  const nextIsAdminRecommended = !!nxtIsAdminRec;
  const queuePatientName = order ? getPatientName(order) : null;
  const nextQueuePatientName = nextOrder ? getPatientName(nextOrder) : null;
  const emergency = emergencyQueue.length > 0;

  const override = {
    display: "block",
    margin: "20% auto",
    borderColor: "red",
  };

  // ─── STEP 4: Render ────────────────────────────────────────────────────────
  if (noShiftRunning) {
    return (
      <div className="show-card-queue">
        <div className="card shadow mb-4 ml-2 mt-3">
          <div
            className="card-body text-center"
            style={{ padding: "5rem 2rem" }}
          >
            <h2 className="mb-3">Tidak Ada Shift yang Sedang Berjalan</h2>
            <p className="mb-0">
              Halaman ini akan otomatis menampilkan antrian begitu shift
              klinik dimulai. Tidak perlu memuat ulang halaman.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="show-card-queue">
      <div className="main-container">
        <div className="queue-card current-queue">
          <div className="card-header">Antrian Sekarang</div>
          <div className="card-content">
            {emergencyQueue.length > 0 && emergency && (
              <div className="emergency-badge">
                <img
                  src={EmergencyImage}
                  alt="EMERGENCY"
                  className="emergency-queue-icon"
                />
                <span>EMERGENCY</span>
              </div>
            )}
            <p className="number">
              <span
                style={{
                  color:
                    emergencyQueue.length > 0 && emergency
                      ? "var(--error-color)"
                      : "inherit",
                }}
              >
                {order || "0"}
              </span>
            </p>
            <span className="card-patient">{queuePatientName || "-"}</span>
            {currentIsAdminRecommended && (
              <div className="mt-2">
                <TagStatus text="Passed" />
              </div>
            )}
          </div>
        </div>

        <div className="table-container current-queue">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <p className="queue-table-title">Antrian Berjalan</p>
            <div className="show-queue-top-bar">
              <ShiftInformation shift={runningShift} />
              <div className="show-queue-dr-status-wrapper">
                <div
                  className={cx("dot-1", {
                    "dr-arrived": doctorArrivalStatus !== 0,
                  })}
                ></div>
                <p className="show-queue-dr-status-text">
                  Dokter {doctorArrivalStatus === 0 ? "Belum" : "Sudah"} di
                  Klinik
                </p>
              </div>
            </div>
          </div>

          <Table
            loading={isLoadingQueues}
            dataSource={getPrimaryQueue(products) ?? []}
            columns={[
              {
                title: "No Antri",
                dataIndex: "queue_number",
                key: "queue_number",
                width: 120,
                render: (text, record) => (
                  <QueueNumber
                    text={text}
                    isAdminRecommended={record?.is_passed}
                  />
                ),
              },
              {
                title: "Nama",
                dataIndex: ["child", "fullname"],
                key: "child.fullname",
                render: (text) => <span>{text || "-"}</span>,
              },
              {
                title: "Status",
                dataIndex: ["queue_status", "name"],
                key: "queue_status.name",
                render: (text) => <TagStatus text={text} />,
              },
            ]}
            pagination={false}
            scroll={{ x: "max-content", y: "max-content" }}
          />
        </div>

        <div className="queue-card next-queue">
          <div className="card-header">Antrian Selanjutnya</div>
          <div className="card-content">
            <p className="number">{nextOrder || "0"}</p>
            <span className="card-patient">{nextQueuePatientName || "-"}</span>
            {nextIsAdminRecommended && (
              <div className="mt-2">
                <TagStatus text="Passed" />
              </div>
            )}
          </div>
        </div>

        <div className="queue-tables-column-bottom">
          <div className="table-container passed-queue">
            <p className="queue-table-title">
              Antrian Terlewat ({passedCount})
            </p>
            {/* Gunakan passedQueuesList dari antrian_terlewat di response */}
            <Table
              loading={isLoadingQueues}
              dataSource={passedQueuesList}
              columns={[
                {
                  title: "No Antri",
                  dataIndex: "queue_number",
                  key: "queue_number",
                  width: 120,
                  render: (text, record) => (
                    <QueueNumber
                      text={text}
                      isAdminRecommended={record?.is_passed}
                    />
                  ),
                },
                {
                  title: "Nama",
                  dataIndex: ["child", "fullname"],
                  key: "child.fullname",
                  render: (text) => <span>{text || "-"}</span>,
                },
                {
                  title: "Status",
                  dataIndex: ["queue_status", "name"],
                  key: "queue_status.name",
                  render: (text) => <TagStatus text={text} />,
                },
              ]}
              pagination={false}
              scroll={{ x: "max-content", y: "max-content" }}
            />
            <PassedQueueMore
              passedCount={passedCount}
              shownCount={passedQueuesList.length}
            />
          </div>

          {/* Tampilkan notice antrian terlewat dari response API */}
          {!isLoadingQueues && passedQueueNotice && (
            <div className="queue-notice">
              <div className="queue-notice-content">
                <span className="queue-notice-text">{passedQueueNotice}</span>
                <span className="queue-notice-text">{passedQueueNotice}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Queues;
